import ApiService from '@/services/api.service';
import {
  GET_INTEGRATION_HEALTH,
  ActionApiRouteMapper,
} from '@/stores/actions.type';
import {
  SET_INTEGRATION_HEALTH,
  SET_INTEGRATION_HEALTH_LOADING,
  DISMISS_INTEGRATION_HEALTH_ALERT,
} from '@/stores/mutations.type';
import { replaceEndpointSlugsFromState } from '@/utils';

const state = {
  integrationHealthData: [],
  isLoading: false,
  lastChecked: null,
  dismissedAlerts: [],
};

const getters = {
  integrationHealthData: (state, getters, rootState) => {
    const currentUser = rootState.advisor?.currentUser;
    if (!currentUser?.integrations) {
      return [];
    }
    
    return currentUser.integrations.map(integration => ({
      integrationType: integration.integrationType,
      integrationName: integration.integrationConfig?.name,
      health: integration.health || { status: 'healthy', lastChecked: new Date() },
    }));
  },
  
  isLoading: (state) => state.isLoading,
  lastChecked: (state) => state.lastChecked,
  
  unhealthyIntegrations: (state, getters) => {
    const healthData = getters.integrationHealthData;
    return healthData.filter(integration => {
      const isUnhealthy = integration.health?.status === 'error' || integration.health?.status === 'warning';
      const isDismissed = state.dismissedAlerts.includes(`${integration.integrationType}-${integration.integrationName}`);
      return isUnhealthy && !isDismissed;
    });
  },

  hasVisibleHealthIssues: (state, getters) => {
    return getters.unhealthyIntegrations.length > 0;
  },

  getIntegrationHealth: (state, getters) => (integrationType, integrationName) => {
    const healthData = getters.integrationHealthData;
    return healthData.find(
      integration => 
        integration.integrationType === integrationType && 
        integration.integrationName === integrationName
    );
  },
};

const actions = {
  /**
   * Fetch integration health data
   */
  async [GET_INTEGRATION_HEALTH]({ commit, rootState }) {
    commit(SET_INTEGRATION_HEALTH_LOADING, true);

    try {
      const url = replaceEndpointSlugsFromState(
        ActionApiRouteMapper[GET_INTEGRATION_HEALTH],
        rootState
      );
      const response = await ApiService.get(url, GET_INTEGRATION_HEALTH);
      commit(SET_INTEGRATION_HEALTH, response);
      return response;
    } catch (error) {
      console.error('Failed to fetch integration health:', error);
      throw error;
    } finally {
      commit(SET_INTEGRATION_HEALTH_LOADING, false);
    }
  },

  /**
   * Dismiss a health alert for a specific integration
   */
  dismissHealthAlert({ commit }, { integrationType, integrationName }) {
    commit(DISMISS_INTEGRATION_HEALTH_ALERT, { integrationType, integrationName });
  },

  /**
   * Start periodic health monitoring
   */
  startHealthMonitoring({ dispatch }) {
    dispatch(GET_INTEGRATION_HEALTH);

    const interval = setInterval(() => {
      dispatch(GET_INTEGRATION_HEALTH);
    }, 5 * 60 * 1000);

    return interval;
  },
};

const mutations = {
  [SET_INTEGRATION_HEALTH](state, healthData) {
    state.integrationHealthData = healthData || [];
    state.lastChecked = new Date();
  },

  [SET_INTEGRATION_HEALTH_LOADING](state, loading) {
    state.isLoading = loading;
  },

  [DISMISS_INTEGRATION_HEALTH_ALERT](state, { integrationType, integrationName }) {
    const alertKey = `${integrationType}-${integrationName}`;
    if (!state.dismissedAlerts.includes(alertKey)) {
      state.dismissedAlerts.push(alertKey);
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
}; 